import { useState } from 'react'
import { Card, CardHeader, CardTitle } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Badge } from '@/components/ui/Badge'
import { Spinner } from '@/components/ui/Spinner'
import { Modal } from '@/components/ui/Modal'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/Tabs'
import {
  useRGPDCategories,
  useDataProcessings,
  useComplianceStats,
  useDataAlerts,
  useRunComplianceAudit,
  useExportComplianceReport,
} from '@/hooks/useRGPD'
import { formatDate } from '@/lib/utils'
import {
  complianceStatusLabels,
  complianceStatusColors,
  legalBasisLabels,
  dataCategoryLabels,
  severityColors,
} from '@/types/rgpd'
import type { DataProcessing, LegalBasis, DataCategory, ComplianceStatus } from '@/types/rgpd'
import {
  Shield,
  Scale,
  UserCheck,
  Clock,
  FileText,
  CheckCircle,
  AlertTriangle,
  AlertCircle,
  ChevronRight,
  RefreshCw,
  Download,
  Plus,
  Eye,
  Edit,
  BarChart3,
  ClipboardCheck,
  Bell,
} from 'lucide-react'

// STORY-046: Page de conformite RGPD

const categoryIcons: Record<string, React.ReactNode> = {
  Scale: <Scale className="w-5 h-5" />,
  UserCheck: <UserCheck className="w-5 h-5" />,
  Shield: <Shield className="w-5 h-5" />,
  Clock: <Clock className="w-5 h-5" />,
  FileText: <FileText className="w-5 h-5" />,
}

export default function RGPDPage() {
  const [activeTab, setActiveTab] = useState('overview')
  const [showProcessingModal, setShowProcessingModal] = useState(false)
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null)
  const [showAuditModal, setShowAuditModal] = useState(false)

  const { data: categories, isLoading: categoriesLoading } = useRGPDCategories()
  const { processings, isLoading: processingsLoading } = useDataProcessings()
  const { data: stats, isLoading: statsLoading } = useComplianceStats()
  const { data: alerts, isLoading: alertsLoading } = useDataAlerts()

  const runAudit = useRunComplianceAudit()
  const exportReport = useExportComplianceReport()

  const handleRunAudit = async () => {
    try {
      const result = await runAudit.mutateAsync()
      setShowAuditModal(true)
    } catch (error) {
      console.error('Erreur lors de l\'audit:', error)
    }
  }

  const handleExportReport = async () => {
    try {
      const report = await exportReport.mutateAsync()
      // Telecharger le rapport
      const blob = new Blob([report], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `rapport-rgpd-${new Date().toISOString().split('T')[0]}.json`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Erreur lors de l\'export:', error)
    }
  }

  const getStatusIcon = (status: ComplianceStatus) => {
    switch (status) {
      case 'compliant':
        return <CheckCircle className="w-4 h-4 text-mint-600" />
      case 'partial':
        return <AlertTriangle className="w-4 h-4 text-gold-600" />
      case 'non_compliant':
        return <AlertCircle className="w-4 h-4 text-red-600" />
      default:
        return <Clock className="w-4 h-4 text-gray-400" />
    }
  }

  const overallCompliance = stats?.overallComplianceRate ?? 0
  const pendingAlerts = alerts?.filter(a => !a.resolved).length ?? 0

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-display font-bold text-brown-900">
            Conformite RGPD
          </h1>
          <p className="mt-1 text-brown-600">
            Gerez la conformite et les traitements de donnees
          </p>
        </div>
        <div className="flex gap-3">
          <Button
            variant="outline"
            leftIcon={<Download className="w-4 h-4" />}
            onClick={handleExportReport}
            isLoading={exportReport.isPending}
          >
            Exporter rapport
          </Button>
          <Button
            leftIcon={<RefreshCw className="w-4 h-4" />}
            onClick={handleRunAudit}
            isLoading={runAudit.isPending}
          >
            Lancer un audit
          </Button>
        </div>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card padding="md" className="flex items-center gap-4">
          <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
            overallCompliance >= 80 ? 'bg-mint-100' : overallCompliance >= 60 ? 'bg-gold-100' : 'bg-red-100'
          }`}>
            <BarChart3 className={`w-6 h-6 ${
              overallCompliance >= 80 ? 'text-mint-600' : overallCompliance >= 60 ? 'text-gold-600' : 'text-red-600'
            }`} />
          </div>
          <div>
            <p className="text-sm text-brown-500">Conformite globale</p>
            <p className="text-2xl font-bold text-brown-900">
              {statsLoading ? '-' : `${overallCompliance}%`}
            </p>
          </div>
        </Card>

        <Card padding="md" className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-lg bg-beige-100 flex items-center justify-center">
            <ClipboardCheck className="w-6 h-6 text-beige-600" />
          </div>
          <div>
            <p className="text-sm text-brown-500">Traitements documentes</p>
            <p className="text-2xl font-bold text-brown-900">
              {statsLoading ? '-' : stats?.totalProcessings || 0}
            </p>
          </div>
        </Card>

        <Card padding="md" className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-lg bg-mint-100 flex items-center justify-center">
            <CheckCircle className="w-6 h-6 text-mint-600" />
          </div>
          <div>
            <p className="text-sm text-brown-500">Demandes traitees</p>
            <p className="text-2xl font-bold text-brown-900">
              {statsLoading ? '-' : stats?.completedRequests || 0}
            </p>
          </div>
        </Card>

        <Card padding="md" className="flex items-center gap-4">
          <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
            pendingAlerts > 0 ? 'bg-red-100' : 'bg-mint-100'
          }`}>
            <Bell className={`w-6 h-6 ${pendingAlerts > 0 ? 'text-red-600' : 'text-mint-600'}`} />
          </div>
          <div>
            <p className="text-sm text-brown-500">Alertes actives</p>
            <p className="text-2xl font-bold text-brown-900">
              {alertsLoading ? '-' : pendingAlerts}
            </p>
          </div>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview">Vue d'ensemble</TabsTrigger>
          <TabsTrigger value="rules">Regles RGPD</TabsTrigger>
          <TabsTrigger value="processings">Traitements</TabsTrigger>
          <TabsTrigger value="alerts">Alertes</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Compliance by category */}
            <Card>
              <CardHeader>
                <CardTitle>Conformite par categorie</CardTitle>
              </CardHeader>
              {categoriesLoading ? (
                <div className="flex justify-center py-8">
                  <Spinner />
                </div>
              ) : (
                <div className="space-y-4">
                  {categories?.map((category) => (
                    <div key={category.id} className="space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="text-brown-600">
                            {categoryIcons[category.icon] || <FileText className="w-5 h-5" />}
                          </span>
                          <span className="font-medium text-brown-900">{category.name}</span>
                        </div>
                        <span className={`text-sm font-medium ${
                          category.complianceRate >= 80 ? 'text-mint-600' :
                          category.complianceRate >= 60 ? 'text-gold-600' : 'text-red-600'
                        }`}>
                          {category.complianceRate}%
                        </span>
                      </div>
                      <div className="h-2 bg-beige-100 rounded-full overflow-hidden">
                        <div
                          className={`h-full transition-all ${
                            category.complianceRate >= 80 ? 'bg-mint-500' :
                            category.complianceRate >= 60 ? 'bg-gold-500' : 'bg-red-500'
                          }`}
                          style={{ width: `${category.complianceRate}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>

            {/* Recent alerts */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Alertes recentes</CardTitle>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setActiveTab('alerts')}
                >
                  Voir tout
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </CardHeader>
              {alertsLoading ? (
                <div className="flex justify-center py-8">
                  <Spinner />
                </div>
              ) : alerts && alerts.length > 0 ? (
                <div className="space-y-3">
                  {alerts.slice(0, 3).map((alert) => (
                    <div
                      key={alert.id}
                      className={`p-3 rounded-lg border ${
                        alert.severity === 'critical' ? 'bg-red-50 border-red-200' :
                        alert.severity === 'high' ? 'bg-gold-50 border-gold-200' :
                        'bg-beige-50 border-beige-200'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <AlertTriangle className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
                          alert.severity === 'critical' ? 'text-red-600' :
                          alert.severity === 'high' ? 'text-gold-600' : 'text-brown-500'
                        }`} />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-brown-900">{alert.title}</p>
                          <p className="text-sm text-brown-600 mt-0.5">{alert.description}</p>
                          <div className="flex items-center gap-2 mt-2">
                            <Badge variant={severityColors[alert.severity].variant} size="sm">
                              {alert.severity === 'critical' ? 'Critique' :
                               alert.severity === 'high' ? 'Haute' :
                               alert.severity === 'medium' ? 'Moyenne' : 'Basse'}
                            </Badge>
                            <span className="text-xs text-brown-500">
                              {alert.affected_records} enregistrement{alert.affected_records > 1 ? 's' : ''} affecte{alert.affected_records > 1 ? 's' : ''}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-brown-500">
                  <CheckCircle className="w-12 h-12 mx-auto mb-3 text-mint-300" />
                  <p className="font-medium">Aucune alerte</p>
                  <p className="text-sm mt-1">Tout est conforme</p>
                </div>
              )}
            </Card>

            {/* Last audit info */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle>Dernier audit</CardTitle>
              </CardHeader>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                  <p className="text-brown-600">
                    {stats?.lastAuditDate
                      ? `Realise le ${formatDate(stats.lastAuditDate)}`
                      : 'Aucun audit realise'}
                  </p>
                  <p className="text-sm text-brown-500 mt-1">
                    Un audit regulier est recommande pour maintenir la conformite RGPD.
                  </p>
                </div>
                <Button
                  leftIcon={<RefreshCw className="w-4 h-4" />}
                  onClick={handleRunAudit}
                  isLoading={runAudit.isPending}
                >
                  Nouvel audit
                </Button>
              </div>
            </Card>
          </div>
        </TabsContent>

        {/* Rules Tab */}
        <TabsContent value="rules">
          {categoriesLoading ? (
            <div className="flex justify-center py-12">
              <Spinner />
            </div>
          ) : (
            <div className="space-y-6">
              {categories?.map((category) => (
                <Card key={category.id}>
                  <CardHeader className="flex flex-row items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        category.complianceRate >= 80 ? 'bg-mint-100' :
                        category.complianceRate >= 60 ? 'bg-gold-100' : 'bg-red-100'
                      }`}>
                        <span className={
                          category.complianceRate >= 80 ? 'text-mint-600' :
                          category.complianceRate >= 60 ? 'text-gold-600' : 'text-red-600'
                        }>
                          {categoryIcons[category.icon] || <FileText className="w-5 h-5" />}
                        </span>
                      </div>
                      <div>
                        <CardTitle>{category.name}</CardTitle>
                        <p className="text-sm text-brown-500">{category.description}</p>
                      </div>
                    </div>
                    <Badge
                      variant={category.complianceRate >= 80 ? 'mint' : category.complianceRate >= 60 ? 'gold' : 'red'}
                    >
                      {category.complianceRate}% conforme
                    </Badge>
                  </CardHeader>

                  <div className="divide-y divide-beige-200">
                    {category.rules.map((rule) => (
                      <div key={rule.id} className="p-4 hover:bg-beige-50 transition-colors">
                        <div className="flex items-start gap-4">
                          <div className="flex-shrink-0 mt-1">
                            {getStatusIcon(rule.status)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className="font-medium text-brown-900">{rule.title}</h4>
                              <Badge variant="gray" size="sm">{rule.article}</Badge>
                            </div>
                            <p className="text-sm text-brown-600">{rule.description}</p>
                            {rule.actions.length > 0 && (
                              <div className="mt-2 flex flex-wrap gap-1">
                                {rule.actions.map((action, index) => (
                                  <span
                                    key={index}
                                    className="inline-flex items-center px-2 py-0.5 text-xs bg-beige-100 text-brown-600 rounded"
                                  >
                                    {action}
                                  </span>
                                ))}
                              </div>
                            )}
                            {rule.notes && (
                              <p className="text-xs text-brown-400 mt-2 italic">{rule.notes}</p>
                            )}
                          </div>
                          <Badge variant={complianceStatusColors[rule.status].variant}>
                            {complianceStatusLabels[rule.status]}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Processings Tab */}
        <TabsContent value="processings">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Registre des traitements</CardTitle>
              <Button
                leftIcon={<Plus className="w-4 h-4" />}
                onClick={() => setShowProcessingModal(true)}
              >
                Nouveau traitement
              </Button>
            </CardHeader>

            {processingsLoading ? (
              <div className="flex justify-center py-12">
                <Spinner />
              </div>
            ) : processings.length === 0 ? (
              <div className="text-center py-12 text-brown-500">
                <FileText className="w-12 h-12 mx-auto mb-3 text-beige-300" />
                <p className="font-medium">Aucun traitement documente</p>
                <p className="text-sm mt-1">
                  Commencez par documenter vos traitements de donnees
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-beige-50 border-b border-beige-200">
                    <tr>
                      <th className="text-left px-6 py-3 text-xs font-medium text-brown-500 uppercase tracking-wider">
                        Traitement
                      </th>
                      <th className="text-left px-6 py-3 text-xs font-medium text-brown-500 uppercase tracking-wider">
                        Base legale
                      </th>
                      <th className="text-left px-6 py-3 text-xs font-medium text-brown-500 uppercase tracking-wider">
                        Categories
                      </th>
                      <th className="text-left px-6 py-3 text-xs font-medium text-brown-500 uppercase tracking-wider">
                        Conservation
                      </th>
                      <th className="text-left px-6 py-3 text-xs font-medium text-brown-500 uppercase tracking-wider">
                        Statut
                      </th>
                      <th className="text-right px-6 py-3 text-xs font-medium text-brown-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-beige-200">
                    {processings.map((processing) => (
                      <tr key={processing.id} className="hover:bg-beige-50">
                        <td className="px-6 py-4">
                          <div>
                            <p className="font-medium text-brown-900">{processing.name}</p>
                            <p className="text-sm text-brown-500 max-w-xs truncate">
                              {processing.purpose}
                            </p>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <Badge variant="brown" size="sm">
                            {legalBasisLabels[processing.legal_basis]}
                          </Badge>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex flex-wrap gap-1">
                            {processing.data_categories.slice(0, 2).map((cat) => (
                              <Badge key={cat} variant="gray" size="sm">
                                {dataCategoryLabels[cat]}
                              </Badge>
                            ))}
                            {processing.data_categories.length > 2 && (
                              <Badge variant="gray" size="sm">
                                +{processing.data_categories.length - 2}
                              </Badge>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-brown-600">
                          {processing.retention_period}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            {getStatusIcon(processing.status)}
                            <Badge variant={complianceStatusColors[processing.status].variant}>
                              {complianceStatusLabels[processing.status]}
                            </Badge>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              className="p-1 text-brown-400 hover:text-brown-600 rounded hover:bg-beige-100"
                              title="Voir les details"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                            <button
                              className="p-1 text-brown-400 hover:text-brown-600 rounded hover:bg-beige-100"
                              title="Modifier"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </TabsContent>

        {/* Alerts Tab */}
        <TabsContent value="alerts">
          <Card padding="none">
            {alertsLoading ? (
              <div className="flex justify-center py-12">
                <Spinner />
              </div>
            ) : alerts && alerts.length > 0 ? (
              <div className="divide-y divide-beige-200">
                {alerts.map((alert) => (
                  <div
                    key={alert.id}
                    className={`p-6 ${alert.resolved ? 'opacity-50' : ''}`}
                  >
                    <div className="flex items-start gap-4">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                        alert.severity === 'critical' ? 'bg-red-100' :
                        alert.severity === 'high' ? 'bg-gold-100' :
                        alert.severity === 'medium' ? 'bg-brown-100' : 'bg-beige-100'
                      }`}>
                        <AlertTriangle className={`w-5 h-5 ${
                          alert.severity === 'critical' ? 'text-red-600' :
                          alert.severity === 'high' ? 'text-gold-600' :
                          alert.severity === 'medium' ? 'text-brown-600' : 'text-beige-600'
                        }`} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="font-medium text-brown-900">{alert.title}</h4>
                          <Badge variant={severityColors[alert.severity].variant} size="sm">
                            {alert.severity === 'critical' ? 'Critique' :
                             alert.severity === 'high' ? 'Haute' :
                             alert.severity === 'medium' ? 'Moyenne' : 'Basse'}
                          </Badge>
                          {alert.resolved && (
                            <Badge variant="mint" size="sm">Resolu</Badge>
                          )}
                        </div>
                        <p className="text-brown-600">{alert.description}</p>
                        <div className="mt-3 p-3 bg-beige-50 rounded-lg">
                          <p className="text-sm font-medium text-brown-700">Action requise:</p>
                          <p className="text-sm text-brown-600">{alert.action_required}</p>
                        </div>
                        <div className="flex items-center gap-4 mt-3 text-sm text-brown-500">
                          <span>{alert.affected_records} enregistrement{alert.affected_records > 1 ? 's' : ''}</span>
                          <span>Entite: {alert.affected_entity}</span>
                          <span>Cree le {formatDate(alert.created_at)}</span>
                        </div>
                      </div>
                      {!alert.resolved && (
                        <Button variant="outline" size="sm">
                          Marquer resolu
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-brown-500">
                <CheckCircle className="w-12 h-12 mx-auto mb-3 text-mint-300" />
                <p className="font-medium">Aucune alerte</p>
                <p className="text-sm mt-1">Toutes les donnees sont conformes</p>
              </div>
            )}
          </Card>
        </TabsContent>
      </Tabs>

      {/* Processing Modal */}
      <Modal
        isOpen={showProcessingModal}
        onClose={() => setShowProcessingModal(false)}
        title="Nouveau traitement"
        size="lg"
      >
        <form className="space-y-4">
          <Input
            label="Nom du traitement"
            placeholder="Ex: Gestion des clients"
            required
          />
          <div>
            <label className="block text-sm font-medium text-brown-700 mb-1">
              Finalite
            </label>
            <textarea
              className="w-full px-4 py-2 border border-beige-300 rounded-lg focus:ring-2 focus:ring-mint-500 focus:border-mint-500"
              rows={3}
              placeholder="Decrivez la finalite de ce traitement..."
            />
          </div>
          <Select label="Base legale">
            <option value="">Selectionnez...</option>
            {Object.entries(legalBasisLabels).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </Select>
          <Input
            label="Duree de conservation"
            placeholder="Ex: 5 ans apres la fin de la relation"
          />
          <div className="flex gap-3 justify-end pt-4">
            <Button
              variant="outline"
              onClick={() => setShowProcessingModal(false)}
            >
              Annuler
            </Button>
            <Button type="submit">
              Creer le traitement
            </Button>
          </div>
        </form>
      </Modal>

      {/* Audit Result Modal */}
      <Modal
        isOpen={showAuditModal}
        onClose={() => setShowAuditModal(false)}
        title="Resultat de l'audit"
      >
        <div className="space-y-4">
          <div className="text-center py-4">
            <div className={`w-16 h-16 mx-auto rounded-full flex items-center justify-center ${
              overallCompliance >= 80 ? 'bg-mint-100' : overallCompliance >= 60 ? 'bg-gold-100' : 'bg-red-100'
            }`}>
              {overallCompliance >= 80 ? (
                <CheckCircle className="w-8 h-8 text-mint-600" />
              ) : (
                <AlertTriangle className={`w-8 h-8 ${overallCompliance >= 60 ? 'text-gold-600' : 'text-red-600'}`} />
              )}
            </div>
            <h3 className="mt-4 text-xl font-bold text-brown-900">
              Score de conformite: {overallCompliance}%
            </h3>
            <p className="text-brown-600 mt-1">
              {overallCompliance >= 80
                ? 'Votre systeme est globalement conforme au RGPD.'
                : overallCompliance >= 60
                ? 'Quelques ameliorations sont necessaires.'
                : 'Des actions correctives urgentes sont requises.'}
            </p>
          </div>
          <div className="border-t border-beige-200 pt-4">
            <p className="text-sm text-brown-500 mb-2">Resume:</p>
            <ul className="space-y-1 text-sm">
              <li className="flex justify-between">
                <span>Traitements documentes</span>
                <span className="font-medium">{stats?.totalProcessings || 0}</span>
              </li>
              <li className="flex justify-between">
                <span>Alertes actives</span>
                <span className="font-medium">{pendingAlerts}</span>
              </li>
              <li className="flex justify-between">
                <span>Demandes en attente</span>
                <span className="font-medium">{stats?.pendingRequests || 0}</span>
              </li>
            </ul>
          </div>
          <Button
            className="w-full"
            onClick={() => {
              setShowAuditModal(false)
              handleExportReport()
            }}
            leftIcon={<Download className="w-4 h-4" />}
          >
            Telecharger le rapport complet
          </Button>
        </div>
      </Modal>
    </div>
  )
}
